/**
 * 학원 목록(/academy) 상단 "학원 가기 전 연습하기 좋은 실내운전연습장" 추천 규칙.
 *
 * - 기준점: 지역 문자열("시도 시군구 읍면동")의 대표 좌표(백엔드 findCoordsByRegion)
 * - 후보: 기준점에서 반경 RECOMMEND_STORE_RADIUS_KM 이내의 직영점·가맹점
 * - 선택: 반경 안에 직영점이 있으면 가장 가까운 직영점, 없으면 가장 가까운 가맹점 1곳
 * - 후보가 없으면 null → 추천 영역 대신 광고 배너(academy_search_top)를 노출한다.
 *
 * 운전연수 사이트(web.dztraining)와 같은 규칙이다 — 반경·우선순위를 바꿀 때는 두 사이트를
 * 함께 맞춘다. 서버 프리페치(page.tsx)와 클라이언트 훅이 같은 키·조회를 쓰도록 여기서만 정의한다.
 */
import { slugify } from '@/lib/utils';
import { QUERY_KEYS } from '@/services/base-api';
import { getStoreList } from '@/services/store.service';
import {
  GetStoreListParams,
  StoreEntity,
  StoreType,
} from '@/services/types/store.type';

export const RECOMMEND_STORE_RADIUS_KM = 20;

const RECOMMEND_STORE_TYPES = [StoreType.DIRECT, StoreType.CHAIN];

/** 반경 안 매장 전체를 거리순으로 받는다(전국 매장 수가 적어 한 페이지로 충분). */
const RECOMMEND_STORE_FETCH_LIMIT = 50;

export function buildRecommendedStoreParams(
  region: string,
): GetStoreListParams {
  return {
    page: 1,
    limit: RECOMMEND_STORE_FETCH_LIMIT,
    regionSort: region,
    maxDistanceKm: RECOMMEND_STORE_RADIUS_KM,
    types: RECOMMEND_STORE_TYPES,
  };
}

/** 거리순 목록에서 직영점 우선으로 1곳을 고른다. */
export function pickRecommendedStore(
  stores: StoreEntity[] | undefined,
): StoreEntity | null {
  if (!stores?.length) return null;
  return (
    stores.find((store) => store.type === StoreType.DIRECT) ??
    stores.find((store) => store.type === StoreType.CHAIN) ??
    null
  );
}

/** 지역 목록(/v1/academy/region) 항목 중 기준 지역 계산에 쓰는 필드 */
interface FilterRegionItem {
  id: number;
  name: string;
  subRegions?: FilterRegionItem[];
}

/**
 * 시/도·시/군/구 필터 주소(?siDo=…&siGunGu=…, 값은 지역 id)를 추천 기준 지역으로 바꾼다.
 * 이름은 클라이언트가 필터를 되살린 뒤 쓰는 기준 지역("시도 시군구")과 같아야 같은 지점이 나온다.
 * 시/도를 못 찾으면 undefined, 시/군/구를 못 찾으면 시/도만 쓴다(클라이언트 복원과 같은 규칙).
 */
export function resolveFilterRegion(
  regions: FilterRegionItem[] | undefined,
  siDo?: string | null,
  siGunGu?: string | null,
): { name: string; siDo: number; siGunGu?: number } | undefined {
  const sido = regions?.find((region) => String(region.id) === siDo);
  if (!sido) return undefined;
  const sigungu = sido.subRegions?.find(
    (region) => String(region.id) === siGunGu,
  );
  return {
    name: [sido.name, sigungu?.name].filter(Boolean).join(' '),
    siDo: sido.id,
    siGunGu: sigungu?.id,
  };
}

/** 서버 프리페치와 클라이언트 훅이 같은 키를 써야 하이드레이션이 맞물린다. */
export function recommendedStoreQueryKey(region?: string) {
  return [QUERY_KEYS.STORE, 'recommended', region] as const;
}

/** 추천 매장 1곳 조회. 반경 내 매장이 없으면 null. */
export async function fetchRecommendedStore(
  region: string,
): Promise<StoreEntity | null> {
  const response = await getStoreList(buildRecommendedStoreParams(region));
  return pickRecommendedStore(response.data?.stores);
}

/**
 * 추천 영역·JSON-LD에 쓰는 매장명. 지점명('부평역점')만으로는 학원 목록 사이에서
 * 무엇인지 알 수 없어 브랜드를 붙인다.
 */
export function getRecommendedStoreDisplayName(store: StoreEntity): string {
  return store.name.startsWith('드라이빙존')
    ? store.name
    : `드라이빙존 ${store.name}`;
}

/**
 * 추천 배너로 들어온 지점 상세 진입 표시. 상세에서 이 값이 있으면 추천 요금제를 노출한다.
 * 지점 상세 canonical은 쿼리를 빼고 만들기 때문에 색인 URL은 늘지 않는다.
 */
export const RECOMMEND_ENTRY_PARAM = { key: 'from', value: 'academy' } as const;

/** 추천 요금제 카드에서 신청한 상담예약의 진입 경로(store_reserve.entry_source) */
export const RECOMMEND_ENTRY_SOURCE = 'academy_recommend';

/** 추천 배너 → 지점 상세 링크(진입 표시 포함) */
export function buildRecommendedStoreHref(store: StoreEntity): string {
  return `/branch/${slugify(store.name, store.id)}?${RECOMMEND_ENTRY_PARAM.key}=${RECOMMEND_ENTRY_PARAM.value}`;
}

/** "인천광역시 미추홀구 주안동" → "주안동" (배너 문구의 기준 지역 표기) */
export function getRegionShortLabel(region: string): string {
  const tokens = region.trim().split(/\s+/);
  return tokens[tokens.length - 1] ?? region;
}
